import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Mode } from '@web/contexts/appModeContext'
import { Animated, NativeScrollEvent, NativeSyntheticEvent, ScrollView, View } from 'react-native'

import { isWeb } from '@common/config/env'
import useDebounce from '@common/hooks/useDebounce'
import useNavigation from '@common/hooks/useNavigation'
import useTheme from '@common/hooks/useTheme'
import useWindowSize from '@common/hooks/useWindowSize'
import DashboardSidePanel from '@web/modules/dashboard/components/DashboardSidePanel'
import PendingActionWindowModal from '@common/modules/dashboard/components/PendingActionWindowModal'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { getUiType } from '@web/utils/uiType'
import { FEATURES } from '@web/constants/features'

import DAppFooter from '../components/DAppFooter'
import DashboardOverview from '../components/DashboardOverview'
import DashboardPages from '../components/DashboardPages'
import getStyles from './styles'

import Header from '@web/components/Header'
import Text from '@common/components/Text'
import PureSpinner from '@common/assets/svg/PureSpinner'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useWalletStateController from '@web/hooks/useWalletStateController'
import { isExtension } from '@web/constants/browserapi'
import { EXTENSION_PRIVATE_CHAIN_IDS } from '@web/constants/extensionChains'
import useAppMode from '@web/hooks/useAppMode'
import { ModeEnum } from '@web/contexts/appModeContext'
import useExtensionAwareNetworks from '@web/hooks/useExtensionAwareNetworks'
import useRailgunContext from '@web/hooks/useRailgunContext'
import useRailgunBatchUpdate from '@web/hooks/useRailgunBatchUpdate'
import { getBalanceUsdByChain } from '@web/utils/railgunTokens'
import useKeystoreControllerState from '@web/hooks/useKeystoreControllerState'
import { computePrivateModeReason, isPrivateModeBlocked } from '@web/utils/privateModeAvailability'
import {
  resolveDashboardNetworkFilterForModeChange,
  resolveExtensionPrivateInvariantFilter
} from '@common/modules/dashboard/utils/dashboardModeNetworkFilter'

const { isPopup, isTab } = getUiType()

// Two-column full-tab grid, mirroring the web app's desktop home and the
// Send / Shield / Unshield tab screens: the existing dashboard column on the
// left, the side panel (allocation donut + recent activity) on the right.
// Widths live in ./styles and match the mobile-web home grid (LAYOUT in
// @mobile/modules/dashboard/screens/home/tokens) so the two builds line up.
// Below this window width the left column would be squeezed too narrow, so the
// dashboard falls back to the single-column layout (the popup always does).
const TWO_COLUMN_MIN_WIDTH = 1100

export const OVERVIEW_CONTENT_MAX_HEIGHT = 120

const DashboardScreen = () => {
  const { mode, setMode } = useAppMode()
  const { styles, theme } = useTheme(getStyles)
  const { maxWidthSize } = useWindowSize()
  // Two-column layout: full-page tab only, and only once the window is wide
  // enough for both columns. The popup stays single-column.
  const withSidePanel = isTab && maxWidthSize(TWO_COLUMN_MIN_WIDTH)
  const { navigate } = useNavigation()
  // The Gas Tank pill used to open an informational bottom sheet that only led
  // to the top-up screen. It now goes straight there — the top-up screen shows
  // the same balance + guidance (two-column side panel in a wide tab, a
  // header guide sheet in the popup), so the extra hop bought nothing. Mirrors
  // the mobile-web GasTankPill, which navigates directly as well.
  const openGasTank = useCallback(() => navigate(WEB_ROUTES.topUpGasTank), [navigate])
  const lastOffsetY = useRef(0)
  const scrollUpStartedAt = useRef(0)
  const [dashboardOverviewSize, setDashboardOverviewSize] = useState({
    width: 0,
    height: 0
  })
  const debouncedDashboardOverviewSize = useDebounce({ value: dashboardOverviewSize, delay: 100 })

  const animatedOverviewHeight = useRef(new Animated.Value(OVERVIEW_CONTENT_MAX_HEIGHT)).current

  const { account, dashboardNetworkFilter } = useSelectedAccountControllerState()
  const { dispatch } = useBackgroundService()
  // Hide-balance ("eye" affordance in the overview header) owns every area of the
  // dashboard that can reveal the portfolio's value — the headline balance and
  // the side panel's allocation donut — so the two never disagree.
  // Persisted in WalletStateController so it survives leaving the dashboard (Trello #509).
  const { isPrivacyModeEnabled } = useWalletStateController()
  const balanceHidden = !!isPrivacyModeEnabled
  const toggleBalanceHidden = useCallback(
    () => dispatch({ type: 'TOGGLE_PRIVACY_MODE' }),
    [dispatch]
  )
  const { networks } = useExtensionAwareNetworks()
  const {
    currentRailgunWallet,
    allRailgunWallets,
    isLoading: railgunIsLoading
  } = useRailgunContext()
  const { railgunEngineInitializing, railgunEngineInitializingError, ensureRailgunEngine } =
    useAppMode()
  const { keys, seeds } = useKeystoreControllerState()
  const batch = useRailgunBatchUpdate()

  const hasRailgunWallet = useMemo(() => {
    return allRailgunWallets.some((wallet) => wallet.accountAddr === account?.addr)
  }, [allRailgunWallets, account?.addr])

  const privateModeReason = useMemo(() => {
    const base = computePrivateModeReason({
      account,
      keys,
      seedIds: seeds?.map((s) => s.id),
      hasRailgunWallet
    })
    // Reflect ephemeral engine states for accounts that *would* be available once setup
    // finishes. Only override `creatable` — never replace 'no-seed' / 'hardware' / 'view-only',
    // which are structural and don't depend on engine state.
    if (base !== 'creatable') return base
    if (railgunEngineInitializingError) return 'failed'
    if (railgunEngineInitializing) return 'initializing'
    return base
  }, [
    account,
    keys,
    seeds,
    hasRailgunWallet,
    railgunEngineInitializing,
    railgunEngineInitializingError
  ])

  const firstPrivateSupportedChainId = useMemo(() => {
    const set = new Set(EXTENSION_PRIVATE_CHAIN_IDS.map((id) => id.toString()))
    const networkChainIdToExistsMap: Record<string, true> = {}
    networks.forEach((n) => {
      networkChainIdToExistsMap[n.chainId.toString()] = true
    })

    const balanceTokenMap = currentRailgunWallet?.balanceTokenMap
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    const railgunUsdByChain =
      balanceTokenMap && prices ? getBalanceUsdByChain(balanceTokenMap, prices) : undefined

    const sortedPrivateChainIds = networks
      .map((n) => n.chainId.toString())
      .filter((chainId) => set.has(chainId) && networkChainIdToExistsMap[chainId])
      .sort((a, b) => {
        const aUsd = railgunUsdByChain?.get(Number(a)) ?? 0
        const bUsd = railgunUsdByChain?.get(Number(b)) ?? 0
        return bUsd - aUsd
      })

    if (sortedPrivateChainIds.length) return sortedPrivateChainIds[0]
    return EXTENSION_PRIVATE_CHAIN_IDS[0]?.toString()
  }, [currentRailgunWallet?.balanceTokenMap, currentRailgunWallet?.balanceTokenPriceMap, networks])

  const handleModeChange = useCallback(
    (nextMode: Mode) => {
      const nextFilter = resolveDashboardNetworkFilterForModeChange({
        previousMode: mode,
        nextMode,
        currentFilter: dashboardNetworkFilter,
        isExtension,
        firstPrivateSupportedChainId
      })
      if (nextFilter !== undefined) {
        dispatch({
          type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
          params: { dashboardNetworkFilter: nextFilter }
        })
      }
      if (nextMode === ModeEnum.PRIVATE) {
        // Wait for the dashboard network filter to be set before switching to private mode.
        setTimeout(() => {
          setMode(nextMode)
        }, 50)
      } else {
        setMode(nextMode)
      }
    },
    [mode, dashboardNetworkFilter, dispatch, firstPrivateSupportedChainId, setMode]
  )

  // If the user is in private mode and doesn't have a railgun wallet (could be hardware wallet), switch to public mode.
  // Gate on `railgunIsLoading=false` so transient empty states during the railgun wallet
  // load loop (or controller-state replacements on mobile-web) don't bounce the user
  // back to PUBLIC mid-session.
  // Also bounce a view-only / hardware / raw-private-key account out of private
  // mode even when a stale railgun wallet row exists for its address (so
  // hasRailgunWallet alone wouldn't trigger the bounce).
  useEffect(() => {
    if (mode !== ModeEnum.PRIVATE) return
    if (railgunIsLoading) return
    // Engine + wallet are created on demand when the user opts into private mode,
    // so there is a window where mode is PRIVATE and no Railgun wallet exists yet.
    // 'creatable' (pre-boot) and 'initializing' (booting) are exactly that window —
    // bouncing here would snap the toggle back to public mid-setup.
    if (privateModeReason === 'creatable' || privateModeReason === 'initializing') return
    if (hasRailgunWallet && !isPrivateModeBlocked(privateModeReason)) return
    handleModeChange(ModeEnum.PUBLIC)
  }, [hasRailgunWallet, privateModeReason, mode, handleModeChange, railgunIsLoading])

  // When opening the dashboard in private mode (via URL or async dispatch), ensure the network filter is set to a valid chain.
  useEffect(() => {
    if (!isExtension || mode !== ModeEnum.PRIVATE || !firstPrivateSupportedChainId) return

    const next = resolveExtensionPrivateInvariantFilter(
      dashboardNetworkFilter,
      firstPrivateSupportedChainId
    )
    if (next !== undefined) {
      dispatch({
        type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
        params: { dashboardNetworkFilter: next }
      })
    }
  }, [mode, dashboardNetworkFilter, dispatch, firstPrivateSupportedChainId])

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!isPopup) return

      const {
        contentOffset: { y }
      } = event.nativeEvent

      if (scrollUpStartedAt.current === 0 && lastOffsetY.current > y) {
        scrollUpStartedAt.current = y
      } else if (scrollUpStartedAt.current > 0 && y > lastOffsetY.current) {
        scrollUpStartedAt.current = 0
      }
      lastOffsetY.current = y

      // The user has to scroll down the height of the overview container in order make it smaller.
      // This is done, because hiding the overview will subtract the height of the overview from the height of the
      // scroll view, thus a shorter scroll container may no longer be scrollable after hiding the overview
      // and if that happens, the user will not be able to scroll up to expand the overview again.
      const scrollDownThreshold = dashboardOverviewSize.height
      // scrollUpThreshold must be a constant value and not dependent on the height of the overview,
      // because the height will change as the overview animates from small to large.
      const scrollUpThreshold = 200
      const isOverviewExpanded =
        y < scrollDownThreshold || y < scrollUpStartedAt.current - scrollUpThreshold

      Animated.spring(animatedOverviewHeight, {
        toValue: isOverviewExpanded ? OVERVIEW_CONTENT_MAX_HEIGHT : 0,
        bounciness: 0,
        speed: 2.8,
        overshootClamping: true,
        // Drives layout props (paddingTop/paddingBottom/maxHeight) which the native
        // animated module does not support — must run on JS driver on all platforms.
        useNativeDriver: false
      }).start()
    },
    [animatedOverviewHeight, dashboardOverviewSize.height, lastOffsetY, scrollUpStartedAt]
  )

  return (
    <>
      <PendingActionWindowModal />
      <Header
        mode={mode}
        onModeChange={handleModeChange}
        privateModeReason={privateModeReason}
        onRetryRailgunInit={ensureRailgunEngine}
      />
      <View style={styles.container}>
        <View style={withSidePanel ? styles.grid : flexbox.flex1}>
          <View style={[withSidePanel ? styles.mainColumn : flexbox.flex1, spacings.ptSm]}>
            <DashboardOverview
              mode={mode}
              openGasTank={FEATURES.gasTankUiEnabled ? openGasTank : () => {}}
              animatedOverviewHeight={animatedOverviewHeight}
              dashboardOverviewSize={debouncedDashboardOverviewSize}
              setDashboardOverviewSize={setDashboardOverviewSize}
              privateModeReason={privateModeReason}
              balanceHidden={balanceHidden}
              onToggleBalanceHidden={toggleBalanceHidden}
            />
            {mode === ModeEnum.PRIVATE && batch && (
              <View
                style={[
                  flexbox.directionRow,
                  flexbox.alignCenter,
                  flexbox.justifyCenter,
                  spacings.mvTy,
                  {
                    gap: 8
                  }
                ]}
              >
                <PureSpinner style={{ width: 20, height: 20 }} color={theme.primary} />
                <Text fontSize={12} weight="semiBold">
                  {batch.status}
                </Text>
              </View>
            )}
            <DashboardPages onScroll={onScroll} animatedOverviewHeight={animatedOverviewHeight} />
          </View>
          {withSidePanel && (
            <ScrollView
              style={styles.sidePanel}
              contentContainerStyle={spacings.pbSm}
              showsVerticalScrollIndicator={false}
            >
              <DashboardSidePanel mode={mode} balanceHidden={balanceHidden} />
            </ScrollView>
          )}
        </View>
        <DAppFooter />
      </View>
    </>
  )
}

export default React.memo(DashboardScreen)
